import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, LogOut, RefreshCw, Mail, CalendarDays, CreditCard } from "lucide-react";
import { Logo } from "@/components/Logo";
import { inputCls } from "@/components/Contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const PACKAGE_NAMES = {
    starter_savv: "Starter Savv",
    social_savv: "Social Savv",
    brand_savv: "Brand Savv",
    full_savv: "Full Savv",
};

const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
        " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const fmtSlot = (date, time) => {
    const [h, m] = time.split(":").map(Number);
    const t = `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
    const d = new Date(date + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) + " · " + t + " CT";
};

const cardCls = "bg-[#F1F1EF] border border-[#262626] rounded-2xl p-5";

export default function Admin() {
    const [user, setUser] = useState(undefined);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);

    const loadOverview = useCallback(async () => {
        const { data } = await axios.get(`${API}/admin/overview`, { withCredentials: true });
        setData(data);
    }, []);

    useEffect(() => {
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then(async (r) => {
                setUser(r.data);
                await loadOverview();
            })
            .catch(() => setUser(null));
    }, [loadOverview]);

    const login = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await axios.post(
                `${API}/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            setUser(data);
            await loadOverview();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const logout = async () => {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
        setUser(null);
        setData(null);
    };

    if (user === undefined) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 size={30} className="animate-spin text-[#D3A0A7]" />
            </div>
        );
    }

    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center px-5">
                <form
                    onSubmit={login}
                    data-testid="admin-login-form"
                    className="w-full max-w-sm bg-[#F1F1EF] border border-[#262626] rounded-[2rem] p-9 shadow-[0_25px_60px_-25px_rgba(12,12,12,0.2)]"
                >
                    <div className="flex justify-center mb-6">
                        <Logo />
                    </div>
                    <p className="text-center font-hand text-2xl text-[#D3A0A7] mb-6">
                        owners only ♡
                    </p>
                    <div className="space-y-4">
                        <input
                            type="email"
                            required
                            data-testid="admin-email-input"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={inputCls}
                        />
                        <input
                            type="password"
                            required
                            data-testid="admin-password-input"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={inputCls}
                        />
                    </div>
                    {error && (
                        <p data-testid="admin-login-error" className="mt-4 text-sm text-[#D3A0A7]">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="admin-login-submit"
                        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0C0C0C] text-[#F1F1EF] font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-[#D3A0A7] transition-colors duration-300 disabled:opacity-60"
                    >
                        {busy ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
                    </button>
                </form>
            </div>
        );
    }

    const enquiries = data?.enquiries || [];
    const bookings = data?.bookings || [];
    const payments = (data?.payments || []).filter((p) => p.payment_status === "paid");

    return (
        <div data-testid="admin-dashboard" className="min-h-screen px-5 sm:px-8 lg:px-12 py-10">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
                    <div className="flex items-center gap-4">
                        <Logo />
                        <span className="font-hand text-2xl text-[#D3A0A7]">hq</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={loadOverview}
                            data-testid="admin-refresh-btn"
                            className="inline-flex items-center gap-2 border border-[#0C0C0C]/25 text-[#0C0C0C] text-sm font-semibold px-4 py-2.5 rounded-full hover:border-[#D3A0A7] hover:text-[#D3A0A7] transition-colors duration-300"
                        >
                            <RefreshCw size={14} /> Refresh
                        </button>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-btn"
                            className="inline-flex items-center gap-2 border border-[#0C0C0C]/25 text-[#0C0C0C] text-sm font-semibold px-4 py-2.5 rounded-full hover:border-[#D3A0A7] hover:text-[#D3A0A7] transition-colors duration-300"
                        >
                            <LogOut size={14} /> Log out
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                    <div className={cardCls} data-testid="admin-stat-enquiries">
                        <Mail size={18} className="text-[#D3A0A7]" />
                        <p className="mt-3 font-serif-ed text-4xl text-[#0C0C0C]">{enquiries.length}</p>
                        <p className="text-xs uppercase tracking-[0.18em] text-[#A9A6A0] mt-1">Enquiries</p>
                    </div>
                    <div className={cardCls} data-testid="admin-stat-bookings">
                        <CalendarDays size={18} className="text-[#262626]" />
                        <p className="mt-3 font-serif-ed text-4xl text-[#0C0C0C]">{bookings.length}</p>
                        <p className="text-xs uppercase tracking-[0.18em] text-[#A9A6A0] mt-1">Booked calls</p>
                    </div>
                    <div className={cardCls} data-testid="admin-stat-payments">
                        <CreditCard size={18} className="text-[#D3A0A7]" />
                        <p className="mt-3 font-serif-ed text-4xl text-[#0C0C0C]">{payments.length}</p>
                        <p className="text-xs uppercase tracking-[0.18em] text-[#A9A6A0] mt-1">Pre-payments</p>
                    </div>
                </div>

                <h2 className="font-serif-ed text-2xl text-[#0C0C0C] mb-4">Booked calls</h2>
                <div className="space-y-3 mb-10" data-testid="admin-bookings-list">
                    {bookings.length === 0 && <p className="text-sm text-[#A9A6A0]">No calls booked yet.</p>}
                    {bookings.map((b, i) => (
                        <div key={b.id} className={cardCls} data-testid={`booking-row-${i}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="font-semibold text-[#0C0C0C] text-sm">
                                    {b.name} <span className="text-[#A9A6A0] font-normal">· {b.email}</span>
                                </p>
                                <span className="text-xs font-semibold bg-[#141414] text-[#262626] px-3 py-1 rounded-full border border-[#CFCCC6]/50">
                                    {fmtSlot(b.date, b.time)}
                                </span>
                            </div>
                            {(b.business || b.notes) && (
                                <p className="mt-2 text-sm text-[#A9A6A0]">
                                    {b.business}{b.business && b.notes ? " — " : ""}{b.notes}
                                </p>
                            )}
                            {b.reminder_sent && (
                                <p className="mt-1 text-xs text-[#D3A0A7]">reminder sent ♡</p>
                            )}
                        </div>
                    ))}
                </div>

                <h2 className="font-serif-ed text-2xl text-[#0C0C0C] mb-4">Enquiries</h2>
                <div className="space-y-3 mb-10" data-testid="admin-enquiries-list">
                    {enquiries.length === 0 && <p className="text-sm text-[#A9A6A0]">No enquiries yet.</p>}
                    {enquiries.map((q, i) => (
                        <div key={q.id} className={cardCls} data-testid={`enquiry-row-${i}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="font-semibold text-[#0C0C0C] text-sm">
                                    {q.name} <span className="text-[#A9A6A0] font-normal">· {q.email}</span>
                                </p>
                                <span className="text-xs text-[#A9A6A0]">{fmtDate(q.created_at)}</span>
                            </div>
                            <p className="mt-1 text-xs text-[#D3A0A7]">{[q.business, q.service].filter(Boolean).join(" · ")}</p>
                            <p className="mt-2 text-sm text-[#A9A6A0] leading-relaxed">{q.message}</p>
                        </div>
                    ))}
                </div>

                <h2 className="font-serif-ed text-2xl text-[#0C0C0C] mb-4">Pre-payments</h2>
                <div className="space-y-3" data-testid="admin-payments-list">
                    {payments.length === 0 && <p className="text-sm text-[#A9A6A0]">No pre-payments yet.</p>}
                    {payments.map((p, i) => (
                        <div key={p.session_id} className={cardCls} data-testid={`payment-row-${i}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="font-semibold text-[#0C0C0C] text-sm">
                                    {PACKAGE_NAMES[p.lookup_key] || p.lookup_key}
                                </p>
                                <span className="text-xs font-semibold bg-[#D3A0A7] text-[#F1F1EF] px-3 py-1 rounded-full">
                                    ${(p.amount / 100).toLocaleString()} · paid
                                </span>
                            </div>
                            {p.customer_email && (
                                <p className="mt-1 text-sm text-[#A9A6A0]">{p.customer_email}</p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
